/**
 * Aroo's own UK number-plate glyph set (A–Z, 0–9), drawn to the proportions of the mandatory
 * Charles Wright 2001 face without using it (that font is not freely licensed).
 *
 * Geometry follows the legal dimensions for 79 mm characters on plates made since 1 Sept 2001
 * (Road Vehicles (Display of Registration Marks) Regulations 2001, Sch. 3; BS AU 145e):
 * every character sits in a 50 × 79 unit box (1 unit = 1 mm), except "1" and "I" which are a
 * single 14 unit stroke; strokes are 14 units wide (monoline).
 *
 * Each glyph is a list of centre-line paths stroked at {@link PLATE_STROKE} units with butt caps
 * and mitred joins, then clipped to the glyph box. Overshooting a path past the box and letting
 * the clip cut it gives the flat, square terminals of the real face (the flat apex of A, the
 * flat foot of V, the cut top of the 4). Keep every path absolute (M L H V A C Z only): the 9 is
 * generated from the 6 by rotating its path, and PDF output re-uses the same strings.
 */

/** Height of a character, in mm. */
export const GLYPH_HEIGHT = 79;
/** Width of every character except "1" and "I", in mm. */
export const GLYPH_WIDTH = 50;
/** Stroke width, in mm. */
export const PLATE_STROKE = 14;
/** Width of the single-stroke "1" and "I". */
export const NARROW_WIDTH = PLATE_STROKE;

export interface Glyph {
  /** Advance width of the glyph box, in mm. */
  width: number;
  /** Centre-line paths, stroked at PLATE_STROKE and clipped to the box (0,0)–(width,79). */
  strokes: string[];
}

const W = GLYPH_WIDTH;
const H = GLYPH_HEIGHT;

/** Straight stroke from (x1,y1) to (x2,y2), extended by `e` units past both ends (clip trims). */
function bar(x1: number, y1: number, x2: number, y2: number, e = 0): string {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const ux = (dx / len) * e;
  const uy = (dy / len) * e;
  return `M${r(x1 - ux)} ${r(y1 - uy)} L${r(x2 + ux)} ${r(y2 + uy)}`;
}

function r(n: number): string {
  return String(Math.round(n * 100) / 100);
}

/** Rotate an absolute M/L/H/V/A/C/Z path by 180° inside a w × h box. */
function rotate180(d: string, w: number, h: number): string {
  const tokens = d.match(/[MLHVACZ]|-?\d*\.?\d+/g) ?? [];
  const out: string[] = [];
  let i = 0;
  const num = () => Number(tokens[i++]);
  while (i < tokens.length) {
    const cmd = tokens[i++]!;
    switch (cmd) {
      case 'M':
      case 'L':
        out.push(`${cmd}${r(w - num())} ${r(h - num())}`);
        break;
      case 'H':
        out.push(`H${r(w - num())}`);
        break;
      case 'V':
        out.push(`V${r(h - num())}`);
        break;
      case 'A': {
        const rx = num();
        const ry = num();
        const rot = num();
        const large = num();
        const sweep = num();
        out.push(`A${rx} ${ry} ${rot} ${large} ${sweep} ${r(w - num())} ${r(h - num())}`);
        break;
      }
      case 'C': {
        const pts = [num(), num(), num(), num(), num(), num()];
        out.push(
          `C${r(w - pts[0]!)} ${r(h - pts[1]!)} ${r(w - pts[2]!)} ${r(h - pts[3]!)} ${r(w - pts[4]!)} ${r(h - pts[5]!)}`,
        );
        break;
      }
      case 'Z':
        out.push('Z');
        break;
      default:
        throw new Error(`rotate180: unsupported command ${cmd}`);
    }
  }
  return out.join(' ');
}

/** Closed rounded rectangle through stroke centres. */
function loop(x1: number, y1: number, x2: number, y2: number, rad: number): string {
  return [
    `M${x1} ${y1 + rad}`,
    `A${rad} ${rad} 0 0 1 ${x1 + rad} ${y1}`,
    `H${x2 - rad}`,
    `A${rad} ${rad} 0 0 1 ${x2} ${y1 + rad}`,
    `V${y2 - rad}`,
    `A${rad} ${rad} 0 0 1 ${x2 - rad} ${y2}`,
    `H${x1 + rad}`,
    `A${rad} ${rad} 0 0 1 ${x1} ${y2 - rad}`,
    'Z',
  ].join(' ');
}

// Stroke-centre guides: the outer edge of a stroke centred on these lands on the box edge.
const L = 7; // left stem centre
const R = 43; // right stem centre
const T = 7; // top bar centre
const B = 72; // bottom bar centre

const SIX = [
  `M44 ${T} H21 A14 14 0 0 0 ${L} 21 V58 A14 14 0 0 0 21 ${B} H29 A14 14 0 0 0 ${R} 58 V50 A14 14 0 0 0 29 36 H9`,
];

const GLYPH_SOURCE: Record<string, Glyph> = {
  A: {
    width: W,
    strokes: [bar(7.1, 79, 23.1, 0, 10), bar(42.9, 79, 26.9, 0, 10), `M8 54 H42`],
  },
  B: {
    width: W,
    strokes: [
      `M10 0 V${H}`,
      `M0 ${T} H28 A11 11 0 0 1 39 18 V27 A11 11 0 0 1 28 38 H10`,
      `M10 38 H31 A12 12 0 0 1 ${R} 50 V60 A12 12 0 0 1 31 ${B} H0`,
    ],
  },
  C: {
    width: W,
    strokes: [
      `M${R} 24 V21 A14 14 0 0 0 29 ${T} H21 A14 14 0 0 0 ${L} 21 V58 A14 14 0 0 0 21 ${B} H29 A14 14 0 0 0 ${R} 58 V55`,
    ],
  },
  D: {
    width: W,
    strokes: [`M10 0 V${H}`, `M0 ${T} H26 A17 17 0 0 1 ${R} 24 V55 A17 17 0 0 1 26 ${B} H0`],
  },
  E: {
    width: W,
    strokes: [`M48 ${T} H${L} V${B} H48`, `M${L} 39 H44`],
  },
  F: {
    width: W,
    strokes: [`M48 ${T} H${L} V${H}`, `M${L} 40 H44`],
  },
  G: {
    width: W,
    strokes: [
      `M${R} 24 V21 A14 14 0 0 0 29 ${T} H21 A14 14 0 0 0 ${L} 21 V58 A14 14 0 0 0 21 ${B} H29 A14 14 0 0 0 ${R} 58 V40 H27`,
    ],
  },
  H: {
    width: W,
    strokes: [`M${L} 0 V${H}`, `M${R} 0 V${H}`, `M${L} 39.5 H${R}`],
  },
  I: { width: NARROW_WIDTH, strokes: [`M7 0 V${H}`] },
  J: {
    width: W,
    strokes: [`M${R} 0 V58 A14 14 0 0 1 29 ${B} H21 A14 14 0 0 1 ${L} 58 V53`],
  },
  K: {
    width: W,
    strokes: [`M${L} 0 V${H}`, bar(7, 60.5, 41.9, 0, 12), bar(21, 36, 42.2, 79, 10)],
  },
  L: { width: W, strokes: [`M${L} 0 V${B} H48`] },
  M: {
    width: W,
    strokes: [`M${L} 0 V${H}`, `M${R} 0 V${H}`, `M6.5 -6 L25 51 L43.5 -6`],
  },
  N: {
    width: W,
    strokes: [`M${L} 0 V${H}`, `M${R} 0 V${H}`, bar(7.5, 0, 42.5, 79, 10)],
  },
  O: { width: W, strokes: [loop(L, T, R, B, 15)] },
  P: {
    width: W,
    strokes: [`M${L} 0 V${H}`, `M${L} ${T} H29 A13 13 0 0 1 42 20 V31 A13 13 0 0 1 29 44 H${L}`],
  },
  Q: {
    width: W,
    strokes: [loop(L, T, R, B, 15), bar(28, 56, 44, 79, 12)],
  },
  R: {
    width: W,
    strokes: [
      `M${L} 0 V${H}`,
      `M${L} ${T} H29 A13 13 0 0 1 42 20 V31 A13 13 0 0 1 29 44 H${L}`,
      `M24 44 L47.4 89`,
    ],
  },
  S: {
    width: W,
    strokes: [
      `M${R} 21 V18 A11 11 0 0 0 32 ${T} H18 A11 11 0 0 0 ${L} 18 V27 A11 11 0 0 0 18 38 H32 A11 11 0 0 1 ${R} 49 V61 A11 11 0 0 1 32 ${B} H18 A11 11 0 0 1 ${L} 61 V58`,
    ],
  },
  T: { width: W, strokes: [`M0 ${T} H${W}`, `M25 ${T} V${H}`] },
  U: {
    width: W,
    strokes: [`M${L} 0 V58 A14 14 0 0 0 21 ${B} H29 A14 14 0 0 0 ${R} 58 V0`],
  },
  V: {
    width: W,
    strokes: [bar(7.1, 0, 23.1, 79, 10), bar(42.9, 0, 26.9, 79, 10)],
  },
  W: {
    width: W,
    strokes: [`M6 -10 L12 76 L25 26 L38 76 L44 -10`],
  },
  X: {
    width: W,
    strokes: [bar(7.5, 0, 42.5, 79, 10), bar(42.5, 0, 7.5, 79, 10)],
  },
  Y: {
    width: W,
    strokes: [bar(7.5, 0, 25, 42, 10), bar(42.5, 0, 25, 42, 10), `M25 40 V${H}`],
  },
  Z: {
    width: W,
    strokes: [`M3 ${T} H43 L${L} ${B} H48`],
  },
  '0': { width: W, strokes: [loop(9, T, 41, B, 14)] },
  '1': { width: NARROW_WIDTH, strokes: [`M7 0 V${H}`] },
  '2': {
    width: W,
    strokes: [
      `M${L} 23 V21 A14 14 0 0 1 21 ${T} H29 A14 14 0 0 1 ${R} 21 V27 A12 12 0 0 1 38.5 36.4 L${L} 61 V${B} H48`,
    ],
  },
  '3': {
    width: W,
    strokes: [
      `M4 ${T} H42 L22 34 H29 A14 14 0 0 1 ${R} 48 V58 A14 14 0 0 1 29 ${B} H21 A14 14 0 0 1 ${L} 58 V56`,
    ],
  },
  '4': {
    width: W,
    strokes: [`M34 0 V${H}`, `M0 57 H${W}`, `M30.8 -7 L4.5 57`],
  },
  '5': {
    width: W,
    strokes: [
      `M47 ${T} H9 L8 37`,
      `M1 36 H29 A14 14 0 0 1 ${R} 50 V58 A14 14 0 0 1 29 ${B} H14 L5 68.5`,
    ],
  },
  '6': { width: W, strokes: SIX },
  '7': { width: W, strokes: [`M2 ${T} H43 L19 85`] },
  '8': { width: W, strokes: [loop(10, T, 40, 38, 12), loop(L, 38, R, B, 14)] },
  '9': { width: W, strokes: SIX.map((d) => rotate180(d, W, H)) },
};

/** The glyph set, keyed by upper-case character. */
export const PLATE_GLYPHS: Readonly<Record<string, Glyph>> = GLYPH_SOURCE;

/** Every character the plate can draw. */
export const PLATE_CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

export function glyphFor(ch: string): Glyph | undefined {
  return PLATE_GLYPHS[ch.toUpperCase()];
}
